// Values read off a document, made safe for Business Central.
//
// Kept apart from BCRequestView so the rules are testable: a date parsed through
// `new Date()` shifted a day in UK summer time and read 05/06/2026 as 6 May, and
// parseFloat("1,250") posted a quantity of 1.

const MONTHS = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11,
  dec: 12, december: 12,
}

const pad = n => String(n).padStart(2, '0')

function iso(year, month, day) {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  const probe = new Date(Date.UTC(year, month - 1, day))
  // Rejects 31 February and the like rather than rolling into March.
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null
  return `${year}-${pad(month)}-${pad(day)}`
}

function fullYear(y) {
  const n = Number(y)
  return y.length === 2 ? 2000 + n : n
}

// 'YYYY-MM-DD' for any date a document is likely to print, or null when it cannot be
// read with confidence. Day-first is assumed for 05/06/2026 (the UK convention this
// product is sold into) unless the numbers can only be read the other way.
export function parseDocumentDate(raw) {
  if (raw === null || raw === undefined) return null
  const s = String(raw).trim()
  if (!s) return null

  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/)
  if (m) return iso(Number(m[1]), Number(m[2]), Number(m[3]))

  m = s.match(/^(\d{4})[/.](\d{1,2})[/.](\d{1,2})$/)
  if (m) return iso(Number(m[1]), Number(m[2]), Number(m[3]))

  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/)
  if (m) {
    const a = Number(m[1])
    const b = Number(m[2])
    const year = fullYear(m[3])
    if (a > 12) return iso(year, b, a) // 25/03/2026 can only be day-first
    if (b > 12) return iso(year, a, b) // 03/25/2026 can only be month-first
    return iso(year, b, a)
  }

  m = s.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)\.?,?\s+(\d{2}|\d{4})$/)
  if (m && MONTHS[m[2].toLowerCase()]) {
    return iso(fullYear(m[3]), MONTHS[m[2].toLowerCase()], Number(m[1]))
  }

  m = s.match(/^([A-Za-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{2}|\d{4})$/)
  if (m && MONTHS[m[1].toLowerCase()]) {
    return iso(fullYear(m[3]), MONTHS[m[1].toLowerCase()], Number(m[2]))
  }

  return null
}

// A number from text a document printed ("£1,250.00", "12,50", "-45"), or `fallback`
// when there is nothing numeric. A decimal comma is only recognised when it is the last
// separator and is followed by one or two digits, so "1,250" stays 1250.
export function toNumber(value, fallback = null) {
  if (value === null || value === undefined || value === '') return fallback
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback
  let s = String(value).trim()
  if (/,\d{1,2}$/.test(s) && s.lastIndexOf(',') > s.lastIndexOf('.')) {
    s = s.replace(/\./g, '').replace(',', '.')
  }
  const cleaned = s.replace(/[^\d.-]/g, '')
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return fallback
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : fallback
}

// Quantity of a line: only a missing value defaults to 1. A stated 0 stays 0 so the
// reviewer sees the problem instead of a silently invented quantity.
export function toQuantity(value) {
  return toNumber(value, 1)
}
